import { useEffect, useRef, useState, type FocusEvent } from 'react';
import { CEW_NAV_ITEMS, CEW_SEARCH_URL, CEW_LINK_TARGET } from '../../lib/cew-site';

/**
 * CewSiteNav — desktop replica of cew.georgetown.edu's own top nav
 * (CR-028, CEW chrome parity spec 2026-08-17). Rendered inside the
 * Topbar's desktop cluster, shown ONLY at viewport >=1136px — the
 * width at which this nav's own row (its 6 items + Subscribe + search)
 * fits on a single line with no wrapping (see Topbar's desktop-cluster
 * doc note for how that floor was measured; CR-027/028/029/030 moved
 * the tool-only Report link out to SectionNav, so it no longer factors
 * into this floor). Below 1136px the Topbar shows the mobile hamburger
 * cluster instead. Every CEW-site link carries `target="_top"` because
 * the tool lives in a full-page iframe (DEC-029) — a plain link would
 * load cew.georgetown.edu *inside* the iframe instead of replacing it.
 *
 * DOM contract: `<nav aria-label="CEW site">` wraps a `<ul>` of
 * `<li>` items. Parents with children (Resource Library / Press
 * Room / About CEW) render BOTH a real `<a>` link to their landing
 * page AND a sibling `<button aria-expanded aria-controls
 * aria-label="Open {label} menu">` chevron toggle. The `<li>` is the
 * hover surface (mouseenter opens; mouseleave closes it ONLY if
 * hover opened it — a click "pins" it open past mouseleave, see
 * `openSourceRef` below). Only one submenu is open at a time; Escape
 * closes and refocuses the trigger button; an outside mousedown, or
 * focus leaving the OPEN item's own `<li>` (see "Focus-out contract"
 * below), also closes it.
 *
 * `<nav>` / `<ul>` / top-level `<li>` all carry `h-full` (2026-08-18
 * follow-up) so a submenu panel's `top-[calc(100%-9px)]` resolves
 * against the Topbar bar's actual bottom edge rather than the `<li>`'s
 * own shorter inline-content box.
 *
 * Wrapping: since the nav only renders where its row fits on one line
 * (>=1136px, enforced by Topbar), every top-level link (incl.
 * Subscribe) carries `whitespace-nowrap`; no resize/measurement
 * state or effects are needed (an earlier such approach pushed this
 * file past the 250-line cap and was reverted 2026-08-18).
 *
 * Chevron target size: h-6 w-6 (was h-4 w-4, below the WCAG 2.5.8
 * 24x24 minimum), `ml-1` gap kept as-is rather than compensated with a
 * negative margin — a negative margin overlapped the sibling `<a>`'s
 * box and tripped the Alfa sia-r113 target-size check. Verified 0
 * A/AA at `ml-1` via `npm run a11y:alfa` against a local preview build.
 *
 * Subscribe pill contrast: text-navy on the pill's gradient start stop
 * (`#F98A40`) measures 4.28:1 (fails WCAG AA); text-dark on the same
 * stop is 6.57:1 and is used instead — the one documented divergence
 * from the measured CEW spec.
 *
 * Focus-out contract (2026-08-18, Frank staging review, APG disclosure-
 * nav fix): closing on `focusout` originally checked only whether the
 * new focus target was contained anywhere in the whole `<nav>` — but a
 * sibling top-level item (e.g. Subscribe) is ALSO inside `<nav>`, so
 * tabbing from an open submenu straight to a sibling top-level link
 * left the submenu open and floating over content, violating the APG
 * disclosure pattern. `handleNavBlur` now closes unless the new focus
 * target is inside the OPEN item's own `<li>` — resolved via
 * `buttonRefs.current[openId]?.closest('li')` rather than a second ref map.
 */

const UNDERLINE_LINK =
  "relative whitespace-nowrap text-base font-medium leading-4 text-white no-underline transition after:absolute after:-bottom-[3px] after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-white after:transition-transform after:content-[''] hover:after:scale-x-100 focus-visible:after:scale-x-100";

const SUBMENU_LINK =
  "relative flex h-[34px] items-center px-[6px] text-base font-medium leading-[22px] text-text-1 no-underline after:absolute after:-bottom-0.5 after:left-[6px] after:h-px after:w-[calc(100%-12px)] after:origin-left after:scale-x-0 after:bg-text-1 after:transition-transform after:content-[''] hover:after:scale-x-100 focus-visible:after:scale-x-100";

export function CewSiteNav() {
  const [openId, setOpenId] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Tracks HOW the open submenu was opened (2026-08-18, e2e-caught fix:
  // a hover-opened menu used to slam shut on the FIRST click of its own
  // chevron). A click while 'hover' just re-tags 'click' (pins it open,
  // does not toggle); a click while already 'click' toggles closed.
  // mouseLeave only closes a 'hover'-sourced menu. Escape / outside
  // mousedown / focus-out / opening another item clear both together.
  const openSourceRef = useRef<'hover' | 'click' | null>(null);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && openId) {
        buttonRefs.current[openId]?.focus();
        openSourceRef.current = null;
        setOpenId(null);
      }
    }
    function handlePointerDown(e: MouseEvent) {
      if (openId && navRef.current && !navRef.current.contains(e.target as Node)) {
        openSourceRef.current = null;
        setOpenId(null);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [openId]);

  // Focus-out contract: see the "Focus-out contract" doc note above.
  function handleNavBlur(e: FocusEvent<HTMLElement>) {
    const openLi = openId ? buttonRefs.current[openId]?.closest('li') : null;
    const related = e.relatedTarget as Node | null;
    if (openId && (!openLi || !related || !openLi.contains(related))) {
      openSourceRef.current = null;
      setOpenId(null);
    }
  }

  function handleItemMouseEnter(itemId: string) {
    openSourceRef.current = 'hover';
    setOpenId(itemId);
  }

  function handleItemMouseLeave(itemId: string) {
    if (openSourceRef.current !== 'hover') return; // click-pinned stays open
    setOpenId((cur) => {
      if (cur !== itemId) return cur;
      openSourceRef.current = null;
      return null;
    });
  }

  function handleChevronClick(itemId: string) {
    if (openId !== itemId) {
      // Fresh click-open (nothing open, or a different item was).
      openSourceRef.current = 'click';
      setOpenId(itemId);
      return;
    }
    if (openSourceRef.current === 'hover') {
      openSourceRef.current = 'click'; // pin open instead of toggling closed
      return;
    }
    openSourceRef.current = null; // already click-opened -- toggle closed
    setOpenId(null);
  }

  return (
    <nav
      ref={navRef}
      aria-label="CEW site"
      className="flex h-full items-center"
      onBlur={handleNavBlur}
    >
      {/* h-full on nav/ul/li: the submenu panel's `top-[calc(100%-9px)]`
          must resolve from the BAR's bottom edge, not the shorter
          inline-content box the <li> would otherwise collapse to (a
          34px content-height li makes `calc(100% - 9px)` land ~25px
          above where the bar actually ends, floating the panel mid-
          header instead of just beneath it). Requires the full
          nav -> ul -> li chain to carry an explicit height. */}
      <ul className="flex h-full items-center gap-x-[30px]">
        {CEW_NAV_ITEMS.map((item) => {
          const hasChildren = (item.children?.length ?? 0) > 0;
          const isOpen = openId === item.id;
          const panelId = `cew-submenu-${item.id}`;
          const isButton = item.variant === 'button';

          return (
            <li
              key={item.id}
              className="relative flex h-full items-center"
              onMouseEnter={hasChildren ? () => handleItemMouseEnter(item.id) : undefined}
              onMouseLeave={hasChildren ? () => handleItemMouseLeave(item.id) : undefined}
            >
              <a
                href={item.href}
                target={CEW_LINK_TARGET}
                rel="noopener"
                className={
                  isButton
                    ? // Subscribe pill — see "Subscribe pill contrast" doc note above.
                      'inline-flex h-10 items-center whitespace-nowrap rounded-pill bg-[linear-gradient(90deg,#F98A40,#F9A21C)] px-6 text-base font-medium text-dark no-underline'
                    : UNDERLINE_LINK
                }
              >
                {item.label}
              </a>
              {hasChildren && (
                <button
                  ref={(el) => {
                    buttonRefs.current[item.id] = el;
                  }}
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  aria-label={`Open ${item.label} menu`}
                  onClick={() => handleChevronClick(item.id)}
                  // Chevron target size — see doc note above.
                  className="ml-1 flex h-6 w-6 items-center justify-center text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                >
                  <svg width={8} height={5} viewBox="0 0 8 5" fill="none" aria-hidden="true">
                    <path
                      d="M1 1l3 3 3-3"
                      stroke="currentColor"
                      strokeWidth={1.4}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}

              {hasChildren && isOpen && (
                <div
                  id={panelId}
                  className="absolute left-[-15px] top-[calc(100%-9px)] w-[240px] rounded-sm bg-white p-5 shadow-panel"
                >
                  <ul>
                    {(item.children ?? []).map((child) => (
                      <li key={child.id}>
                        <a href={child.href} target={CEW_LINK_TARGET} rel="noopener" className={SUBMENU_LINK}>
                          {child.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <a
        href={CEW_SEARCH_URL}
        target={CEW_LINK_TARGET}
        rel="noopener"
        aria-label="Search cew.georgetown.edu"
        className="ml-[28px] flex h-[21px] w-6 items-center justify-center text-white"
      >
        <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </a>
    </nav>
  );
}
